import React, { useState } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'Managed Project Solution',
    scope: '',
    // Honeypot field (should remain empty)
    _subject_alt: ''
  });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Security check: Honeypot trap for bots
    if (formData._subject_alt) {
      console.warn("Spam detected via honeypot.");
      setSubmitted(true); // Silent failure for bots
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submissionData = new FormData();
      submissionData.append("access_key", "8a58dc60-062a-45c7-aaba-d50cab4fae3b");
      submissionData.append("name", formData.name);
      submissionData.append("email", formData.email);
      submissionData.append("subject", `New Inquiry from ${formData.name} [${formData.type}]`);
      submissionData.append("from_name", "Hunargrid Web Portal");
      submissionData.append("message", `
        Type: ${formData.type}
        Scope: ${formData.scope}
      `);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: submissionData
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          onClose();
        }, 10000);
      } else {
        setError("Submission failed. Please try again or contact support@hunargrid.com directly.");
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose}></div>
      <div className="relative w-full max-w-xl bg-white rounded-sm shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className="bg-[#0091da] h-1.5 w-full"></div>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900 z-10">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <div className="p-6 md:p-12">
          {submitted ? (
            <div className="text-center py-12 md:py-20 animate-in slide-in-from-bottom-4">
              <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Inquiry Received</h3>
              <p className="text-slate-600 font-light text-lg mb-8 leading-relaxed">
                Your brief has been successfully routed to our <span className="font-bold text-slate-900">Engineering Council</span>. A partner will contact you within 4 business hours.
              </p>
              
              <div className="bg-slate-50 p-6 rounded-sm border border-slate-100 mb-8 text-left">
                <p className="text-[10px] mono uppercase text-slate-400 mb-4 tracking-widest text-center">Live Reference ID: HG-{Math.floor(Math.random() * 10000)}</p>
                <div className="flex flex-col space-y-3">
                  <div className="text-slate-500 text-xs flex justify-between">
                    <span>Priority Level:</span>
                    <span className="text-slate-900 font-bold uppercase tracking-widest">Enterprise</span>
                  </div>
                  <div className="text-slate-500 text-xs flex justify-between">
                    <span>Assigned Queue:</span>
                    <span className="text-slate-900 font-bold uppercase tracking-widest">Solutions_Architect</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={onClose}
                className="mono text-[10px] uppercase tracking-widest text-slate-400 font-bold hover:text-slate-900"
              >
                Return to Dashboard
              </button>
            </div>
          ) : (
            <>
              <div className="mono text-[10px] text-[#0091da] font-bold uppercase tracking-[0.3em] mb-4">// Technical Briefing</div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-950 mb-8 tracking-tight">Request a Discovery Session</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                {/* Honeypot field: Hidden from humans */}
                <input 
                  type="text" 
                  name="_subject_alt" 
                  tabIndex={-1} 
                  autoComplete="off" 
                  style={{ display: 'none' }} 
                  value={formData._subject_alt}
                  onChange={(e) => setFormData({...formData, _subject_alt: e.target.value})}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="mono text-[9px] uppercase text-slate-400 mb-2 block font-bold tracking-widest">Full Name</label>
                    <input 
                      required 
                      type="text" 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-slate-50 border border-slate-200 p-3 md:p-4 text-sm focus:border-[#0091da] focus:outline-none transition-all placeholder:text-slate-300" 
                      placeholder="John Doe" 
                    />
                  </div>
                  <div>
                    <label className="mono text-[9px] uppercase text-slate-400 mb-2 block font-bold tracking-widest">Corporate Email</label>
                    <input 
                      required 
                      type="email" 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-slate-50 border border-slate-200 p-3 md:p-4 text-sm focus:border-[#0091da] focus:outline-none transition-all placeholder:text-slate-300" 
                      placeholder="john@enterprise.com" 
                    />
                  </div>
                </div>
                
                <div>
                  <label className="mono text-[9px] uppercase text-slate-400 mb-2 block font-bold tracking-widest">Inquiry Type</label>
                  <select 
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-200 p-3 md:p-4 text-sm focus:border-[#0091da] focus:outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option>Managed Project Solution</option>
                    <option>Talent Augmentation</option>
                    <option>Architectural Review</option>
                    <option>Other / General</option>
                  </select>
                </div>

                <div>
                  <label className="mono text-[9px] uppercase text-slate-400 mb-2 block font-bold tracking-widest">Project Scope Summary</label>
                  <textarea 
                    required 
                    value={formData.scope}
                    onChange={(e) => setFormData({...formData, scope: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-200 p-3 md:p-4 text-sm h-32 focus:border-[#0091da] focus:outline-none transition-all resize-none placeholder:text-slate-300" 
                    placeholder="Briefly describe your scaling or delivery challenges..."
                  ></textarea>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 p-4 text-xs font-bold rounded-sm border border-red-100">
                    {error}
                  </div>
                )}

                <button 
                  disabled={loading}
                  type="submit" 
                  className="w-full bg-slate-900 text-white font-bold py-4 md:py-5 uppercase tracking-widest text-xs hover:bg-[#0091da] transition-all rounded-sm shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Routing to Council...' : 'Submit to Engineering Council'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactModal;